import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';

const AdminPaymentsPage = () => {
  const [paymentData, setPaymentData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sites, setSites] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState({
    totalAmountDue: 0,
    totalPaid: 0,
    totalPending: 0,
    employeeCount: 0
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Payment breakdown modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Override states for the modal
  const [overrides, setOverrides] = useState({
    regularHours: { enabled: false, value: '', original: 0, hasUnsavedChanges: false },
    overtimeHours: { enabled: false, value: '', original: 0, hasUnsavedChanges: false },
    expenses: { enabled: false, value: '', original: 0, hasUnsavedChanges: false }
  });
  
  // Override reason state
  const [overrideReason, setOverrideReason] = useState('');
  
  // Save states
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: 'Confirm Action',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: null,
  });
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });

  // Filter state - Single week selection only (simplified approach)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(getWeekNumber(new Date()));
  
  // Calendar state (single week selection)
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Initialize with current week and fetch sites
  useEffect(() => {
    const today = new Date();
    const currentWeekNum = getWeekNumber(today);
    const currentYearNum = today.getFullYear();
    
    setSelectedYear(currentYearNum);
    setSelectedWeek(currentWeekNum);
    
    // Fetch sites and payment data on component mount
    const loadInitialData = async () => {
      await fetchSites();
    };
    
    loadInitialData();
  }, []);

  // Fetch payment data when filters change (but after sites are loaded)
  useEffect(() => {
    if (sites.length > 0) {
      fetchPaymentData();
    }
  }, [selectedYear, selectedWeek, selectedStatus, sites.length]);
  
  // Open payment breakdown modal
  const openPaymentModal = async (employee) => {
    setSelectedPayment(employee);
    
    // Fetch existing overrides for this employee and period
    try {
      const selectedWeekDates = getWeekDates(selectedYear, selectedWeek);
      
      const response = await axios.get(`${API}/admin/payment-overrides`, {
        params: {
          employee_id: employee.employeeId,
          start_date: selectedWeekDates[0].date,
          end_date: selectedWeekDates[6].date
        }
      });
      
      const existingOverrides = response.data || {};
      
      // Calculate original expenses from raw expense data (not from employee object which may contain overrides)
      const originalExpensesCalculation = employee.expenses ? 
        employee.expenses.reduce((total, expense) => total + expense.amount, 0) : 0;

      // Initialize override states with existing overrides or current values
      // CRITICAL FIX: Always use the corrected employee original values, not old database values
      setOverrides({
        regularHours: { 
          enabled: !!existingOverrides.regular_hours_override,
          value: existingOverrides.regular_hours_override?.toString() || employee.regularHours.toString(),
          original: employee.originalRegularHours, // FIX: Always use corrected employee value
          hasUnsavedChanges: false
        },
        overtimeHours: { 
          enabled: !!existingOverrides.overtime_hours_override,
          value: existingOverrides.overtime_hours_override?.toString() || employee.overtimeHours.toString(),
          original: employee.originalOvertimeHours, // FIX: Always use corrected employee value
          hasUnsavedChanges: false
        },
        expenses: { 
          enabled: !!existingOverrides.expenses_override,
          value: existingOverrides.expenses_override?.toString() || employee.approvedExpenses.toString(),
          original: employee.originalExpenses, // FIX: Always use corrected employee value
          hasUnsavedChanges: false
        }
      });
      
      // Set the override reason from existing override
      setOverrideReason(existingOverrides.reason || '');
    } catch (error) {
      console.error('Error fetching overrides:', error);
      // Initialize with default values if fetching fails  
      // These already use the corrected employee values, so no changes needed
      setOverrides({
        regularHours: { 
          enabled: false, 
          value: employee.regularHours.toString(), 
          original: employee.originalRegularHours,
          hasUnsavedChanges: false
        },
        overtimeHours: { 
          enabled: false, 
          value: employee.overtimeHours.toString(), 
          original: employee.originalOvertimeHours,
          hasUnsavedChanges: false
        },
        expenses: { 
          enabled: false, 
          value: employee.approvedExpenses.toString(), 
          original: employee.originalExpenses,
          hasUnsavedChanges: false
        }
      });
      
      // Reset override reason when initializing with defaults
      setOverrideReason('');
    }
    
    // Reset save states
    setSaveSuccess(false);
    setSaveError(null);
    setShowPaymentModal(true);
  };

  // Close payment breakdown modal
  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedPayment(null);
    setOverrides({
      regularHours: { enabled: false, value: '', original: 0, hasUnsavedChanges: false },
      overtimeHours: { enabled: false, value: '', original: 0, hasUnsavedChanges: false },
      expenses: { enabled: false, value: '', original: 0, hasUnsavedChanges: false }
    });
    setOverrideReason(''); // Reset override reason
    setSaveSuccess(false);
    setSaveError(null);
    setIsSaving(false);
  };

  // Handle override toggle
  const handleOverrideToggle = (field) => {
    setOverrides(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        enabled: !prev[field].enabled,
        hasUnsavedChanges: true,
        // Reset to original value if disabling override
        value: !prev[field].enabled ? prev[field].value : prev[field].original.toString()
      }
    }));
    setSaveSuccess(false); // Clear success message when changes are made
  };

  // Handle override value change
  const handleOverrideValueChange = (field, value) => {
    setOverrides(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value: value,
        hasUnsavedChanges: true
      }
    }));
    setSaveSuccess(false); // Clear success message when changes are made
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return Object.values(overrides).some(override => override.hasUnsavedChanges);
  };
  
  // Check if any overrides are currently active (enabled)
  const hasActiveOverrides = () => {
    return Object.values(overrides).some(override => override.enabled);
  };

  // Save overrides to backend
  const saveOverrides = async () => {
    if (!selectedPayment) return;
    
    // Validate override reason if any overrides are active
    if (hasActiveOverrides() && !overrideReason.trim()) {
      setSaveError('Please provide a reason for the override changes.');
      return;
    }
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const selectedWeekDates = getWeekDates(selectedYear, selectedWeek);
      
      const overrideData = {
        employee_id: selectedPayment.employeeId,
        start_date: selectedWeekDates[0].date,
        end_date: selectedWeekDates[6].date,
        regular_hours_override: overrides.regularHours.enabled ? parseFloat(overrides.regularHours.value) || 0 : null,
        overtime_hours_override: overrides.overtimeHours.enabled ? parseFloat(overrides.overtimeHours.value) || 0 : null,
        expenses_override: overrides.expenses.enabled ? parseFloat(overrides.expenses.value) || 0 : null,
        original_regular_hours: selectedPayment.originalRegularHours, // FIX: Use corrected employee value
        original_overtime_hours: selectedPayment.originalOvertimeHours, // FIX: Use corrected employee value  
        original_expenses: selectedPayment.originalExpenses, // FIX: Use corrected employee value
        reason: hasActiveOverrides() ? overrideReason.trim() : null // Include reason if overrides are active
      };
      
      await axios.post(`${API}/admin/payment-overrides`, overrideData);
      
      // Mark all overrides as saved
      setOverrides(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          updated[key].hasUnsavedChanges = false;
        });
        return updated;
      });
      
      setSaveSuccess(true);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      
      // Refresh payment data to reflect changes
      fetchPaymentData();
      
    } catch (error) {
      console.error('Error saving overrides:', error);
      setSaveError(error.response?.data?.message || 'Failed to save changes. Please try again.');
    }
    
    setIsSaving(false);
  };

  // Helper function to get week number (copied from user money page)
  function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }

  // Helper function to get week dates (ISO week calculation)
  const getWeekDates = (year, weekNum) => {
    // Use UTC dates to match getWeekNumber logic exactly
    const jan1 = new Date(Date.UTC(year, 0, 1));
    const jan1Weekday = jan1.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Convert Sunday (0) to 7 for ISO week calculation
    const jan1Day = jan1Weekday === 0 ? 7 : jan1Weekday;
    
    // Find first Thursday of the year (ISO week 1 contains Jan 4)
    const firstThursday = new Date(Date.UTC(year, 0, 1));
    firstThursday.setUTCDate(1 + (4 - jan1Day + 7) % 7);
    
    // Find the Monday of the first week
    const firstMonday = new Date(firstThursday);
    firstMonday.setUTCDate(firstThursday.getUTCDate() - 3);
    
    // Calculate the start of the requested week
    const weekStart = new Date(firstMonday);
    weekStart.setUTCDate(firstMonday.getUTCDate() + (weekNum - 1) * 7);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setUTCDate(weekStart.getUTCDate() + i);
      dates.push({ date: date.toISOString().split('T')[0], day: date.getUTCDate() });
    }
    return dates;
  };

  // Get week start and end dates for display
  function getWeekStartEnd(year, weekNum) {
    const weekDates = getWeekDates(year, weekNum);
    const startDate = new Date(weekDates[0].date);
    const endDate = new Date(weekDates[6].date);
    
    return {
      start: startDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
      end: endDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })
    };
  }

  // Format week display
  function formatWeekDisplay(year, weekNum) {
    const weekDates = getWeekStartEnd(year, weekNum);
    return `Week ${weekNum} (${weekDates.start} - ${weekDates.end})`;
  }

  // Handle date click in calendar
  function handleDateClick(date) {
    const year = date.getFullYear();
    const weekNum = getWeekNumber(date);
    
    setSelectedYear(year);
    setSelectedWeek(weekNum);
    setShowCalendar(false);
  }

  // Generate calendar for a specific month
  function generateCalendar(currentDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    
    // Adjust to start from Monday (1) instead of Sunday (0)
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);
    
    const weeks = [];
    const currentWeek = [];
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      currentWeek.push(date);
      
      if (currentWeek.length === 7) {
        weeks.push([...currentWeek]);
        currentWeek.length = 0;
      }
    }
    
    return { weeks, month, year };
  }

  // Go to current week
  const goToCurrentWeek = () => {
    const today = new Date();
    const currentWeekNum = getWeekNumber(today);
    const currentYearNum = today.getFullYear();
    
    setSelectedYear(currentYearNum);
    setSelectedWeek(currentWeekNum);
  };

  // Check if currently viewing the current week
  const isCurrentWeekSelected = () => {
    const today = new Date();
    const currentWeekNum = getWeekNumber(today);
    const currentYearNum = today.getFullYear();
    return selectedYear === currentYearNum && selectedWeek === currentWeekNum;
  };

  // Navigate to previous week
  const goToPreviousWeek = () => {
    // Get the current week's Monday and go back 7 days
    const weekDates = getWeekDates(selectedYear, selectedWeek);
    const currentMonday = new Date(weekDates[0].date);
    currentMonday.setDate(currentMonday.getDate() - 7);
    
    // Get the week number and year for this new date
    const newWeekNum = getWeekNumber(currentMonday);
    const newYear = currentMonday.getFullYear();
    
    // Handle edge case where the date might belong to a different ISO year
    // Check if the week makes sense for the year
    if (newWeekNum > 50 && currentMonday.getMonth() === 0) {
      // Week 52/53 in January means it belongs to previous year
      setSelectedYear(newYear - 1);
    } else if (newWeekNum === 1 && currentMonday.getMonth() === 11) {
      // Week 1 in December means it belongs to next year
      setSelectedYear(newYear + 1);
    } else {
      setSelectedYear(newYear);
    }
    setSelectedWeek(newWeekNum);
  };

  // Navigate to next week
  const goToNextWeek = () => {
    // Get the current week's Monday and go forward 7 days
    const weekDates = getWeekDates(selectedYear, selectedWeek);
    const currentMonday = new Date(weekDates[0].date);
    currentMonday.setDate(currentMonday.getDate() + 7);
    
    // Get the week number and year for this new date
    const newWeekNum = getWeekNumber(currentMonday);
    const newYear = currentMonday.getFullYear();
    
    // Handle edge case where the date might belong to a different ISO year
    if (newWeekNum > 50 && currentMonday.getMonth() === 0) {
      // Week 52/53 in January means it belongs to previous year
      setSelectedYear(newYear - 1);
    } else if (newWeekNum === 1 && currentMonday.getMonth() === 11) {
      // Week 1 in December means it belongs to next year
      setSelectedYear(newYear + 1);
    } else {
      setSelectedYear(newYear);
    }
    setSelectedWeek(newWeekNum);
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/admin/sites`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSites(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      return [];
    }
  };

  const fetchPaymentData = async () => {
    setIsLoading(true);
    try {
      
      // Convert selected year/week to date range (single week)
      const selectedWeekDates = getWeekDates(selectedYear, selectedWeek);
      
      const startDate = selectedWeekDates[0].date;
      const endDate = selectedWeekDates[6].date;
      
      // First, fetch real users
      const usersResponse = await axios.get(`${API}/admin/users`);
      const users = usersResponse.data;
      
      // Filter active users only
      const activeUsers = users.filter(user => user.active === true);
      
      if (activeUsers.length === 0) {
        setPaymentData([]);
        setWeeklyStats({ totalAmountDue: 0, totalPaid: 0, totalPending: 0, employeeCount: 0 });
        setIsLoading(false);
        return;
      }

      // Fetch attendance data for the week range
      const attendanceParams = new URLSearchParams();
      attendanceParams.append('start_date', startDate);
      attendanceParams.append('end_date', endDate);

      let attendanceResponse;
      try {
        attendanceResponse = await axios.get(`${API}/admin/attendance?${attendanceParams}`);
      } catch (error) {
        console.warn('Could not fetch attendance data:', error);
        attendanceResponse = { data: [] };
      }
      
      const attendanceData = attendanceResponse.data || [];

      // Fetch approved expenses for the week range with updated logic
      // Only include expenses that were approved in this pay run week or earlier
      let expensesResponse;
      try {
        expensesResponse = await axios.get(`${API}/admin/expenses/approved`, {
          params: {
            start_date: startDate,
            end_date: endDate,
            pay_run_week: startDate  // Pass pay run week to prevent expenses from appearing in earlier pay runs
          }
        });
      } catch (error) {
        console.warn('Could not fetch expenses data:', error);
        expensesResponse = { data: [] };
      }
      
      const expensesData = expensesResponse.data || [];

      // Calculate payment data for each active user using backend API
      const paymentData = await Promise.all(activeUsers.map(async (user) => {
        try {
          // Call backend earnings API for this user and week
          const earningsResponse = await axios.get(`${API}/admin/weekly-earnings`, {
            params: {
              user_id: user.id,
              week_start: startDate,
              week_end: endDate
            },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          
          const earnings = earningsResponse.data;
          
          
          // Get ALL user's approved but UNPAID expenses (sweep up model)
          // Only include expenses approved on or before this week (prevents future-dated)
          const weekEndDate = new Date(endDate);
          
          const userExpenses = expensesData.filter(expense => {
            if (expense.user_id !== user.id && expense.username !== user.username) {
              return false;
            }
            
            // Check status and payment status
            if (expense.status !== 'approved' || expense.payment_status !== 'unpaid') {
              return false;
            }
            
            // Only include expenses approved on or before week end date
            if (expense.approval_date) {
              const approvalDate = new Date(expense.approval_date);
              return approvalDate <= weekEndDate;
            }
            
            return false;  // Exclude if no approval_date
          });
          
          // Get hourly rates
          const hourlyRate = user.gross_hourly_rate || 0;
          const overtimeRate = hourlyRate; // Same as hourly rate
          
          // Use backend-calculated CIS and net pay (NO frontend calculations)
          const grossLabourPay = earnings.gross_labour_pay;
          const cisDeduction = earnings.cis_deduction;
          const finalNetPay = earnings.net_pay;
          
          // Fetch payment status
          let paymentStatus = 'pending';
          let paymentDate = null;
          try {
            const statusResponse = await axios.get(`${API}/admin/payment-status`, {
              params: {
                employee_id: user.id,
                start_date: startDate,
                end_date: endDate
              },
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            
            if (statusResponse.data) {
              paymentStatus = statusResponse.data.payment_status || 'pending';
              paymentDate = statusResponse.data.payment_date || null;
            }
          } catch (error) {
            console.warn(`Could not fetch payment status for user ${user.id}:`, error.message);
          }

          return {
            id: user.id,
            employeeId: user.id,
            employeeName: `${user.first_name} ${user.last_name}`,
            profilePhotoUrl: user.profile_photo_url || null,
            username: user.username,
            role: user.role,
            department: user.department,
            siteName: earnings.sites_worked?.length > 0 ? 'Multiple Sites' : 'No sites worked',
            allSites: earnings.sites_worked || [],
            siteId: '',
            regularHours: earnings.regular_hours,
            overtimeHours: earnings.overtime_hours,
            hourlyRate: hourlyRate,
            overtimeRate: overtimeRate,
            regularPay: earnings.regular_pay,
            overtimePay: earnings.overtime_pay,
            approvedExpenses: earnings.expenses_total,
            totalGrossPay: grossLabourPay,  // Labour only (regular + overtime)
            cisDeduction: cisDeduction,
            totalDeductions: cisDeduction,
            netPay: finalNetPay,
            paymentStatus: paymentStatus,
            paymentDate: paymentDate,
            invoiceNumber: `INV-2025-${String(user.id).padStart(3, '0')}`,
            expenses: userExpenses,
            hasOverrides: earnings.has_override,
            originalRegularHours: earnings.original_values?.regular_hours || earnings.regular_hours,
            originalOvertimeHours: earnings.original_values?.overtime_hours || earnings.overtime_hours,
            originalExpenses: earnings.original_values?.expenses || earnings.expenses_total,
            overrideReason: earnings.override_reason,
            locked_hourly_rate: earnings.locked_hourly_rate,
            using_locked_rate: earnings.using_locked_rate,
            pay_run_reopened: earnings.pay_run_reopened,
            break_hours_deducted: earnings.break_hours_deducted,
            enabled_days_worked: earnings.enabled_days_worked,
            // CIS and user information
            cis_registered: user.cis_registered || false,
            utr_number: user.utr_number || '',
            ni_number: user.ni_number || '',
            address: user.address || '',
            bank_name: user.bank_name || '',
            account_number: user.account_number || '',
            sort_code: user.sort_code || ''
          };
        } catch (error) {
          console.error(`Failed to fetch earnings for user ${user.id}:`, error);
          // Return empty/default data if API fails
          return {
            id: user.id,
            employeeId: user.id,
            employeeName: `${user.first_name} ${user.last_name}`,
            profilePhotoUrl: user.profile_photo_url || null,
            username: user.username,
            regularHours: 0,
            overtimeHours: 0,
            hourlyRate: user.gross_hourly_rate || 0,
            regularPay: 0,
            overtimePay: 0,
            approvedExpenses: 0,
            totalGrossPay: 0,
            netPay: 0,
            paymentStatus: 'pending',
            hasOverrides: false
          };
        }
      }));

      // Filter by status if needed
      let filteredData = paymentData;
      if (selectedStatus !== 'all') {
        filteredData = paymentData.filter(emp => emp.paymentStatus === selectedStatus);
      }

      setPaymentData(filteredData);
      
      // Calculate weekly stats
      const stats = calculateWeeklyStats(filteredData);
      setWeeklyStats(stats);
      
    } catch (error) {
      console.error('Error fetching payment data:', error);
      // Fallback to empty data instead of mock data
      setPaymentData([]);
      setWeeklyStats({ totalAmountDue: 0, totalPaid: 0, totalPending: 0, employeeCount: 0 });
    }
    setIsLoading(false);
  };

  const calculateWeeklyStats = (data) => {
    const stats = data.reduce((acc, employee) => {
      acc.totalAmountDue += employee.netPay;
      if (employee.paymentStatus === 'paid') {
        acc.totalPaid += employee.netPay;
      } else {
        acc.totalPending += employee.netPay;
      }
      return acc;
    }, { totalAmountDue: 0, totalPaid: 0, totalPending: 0, employeeCount: data.length });
    
    return stats;
  };

  const togglePaymentStatus = async (employeeId, currentStatus) => {
    setIsProcessing(true);
    try {
      const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';
      const paymentDate = newStatus === 'paid' ? new Date().toISOString().split('T')[0] : null;
      
      // If reopening a paid run, show warning first
      if (currentStatus === 'paid' && newStatus === 'pending') {
        // Get employee data for warning message
        const employee = paymentData.find(emp => emp.employeeId === employeeId);
        const currentRate = employee?.hourlyRate || 0;
        const hasLockedRate = employee?.locked_hourly_rate != null;
        const lockedRate = hasLockedRate ? employee.locked_hourly_rate : currentRate;
        
        // Show confirmation dialog
        return new Promise((resolve) => {
          setConfirmModal({
            show: true,
            title: 'Reopen Payment Run?',
            variant: 'warning',
            confirmText: 'Reopen & Recalculate',
            message: `⚠️ Reopening this payment run will recalculate all earnings using the CURRENT hourly rate (£${currentRate.toFixed(2)})${hasLockedRate ? ` instead of the original locked rate (£${lockedRate.toFixed(2)})` : ''}, and the CURRENT work schedule + break duration instead of the schedule locked at the time of payment.\n\nThis may result in different payment amounts and cause inconsistencies with expense and overtime payments in this and subsequent pay runs.\n\nDo you want to proceed with recalculation?`,
            onConfirm: async () => {
              setConfirmModal({ show: false, title: 'Confirm Action', message: '', confirmText: 'Confirm', variant: 'danger', onConfirm: null });
              // Proceed with reopening
              await performToggle(employeeId, newStatus, paymentDate);
              resolve(true);
            }
          });
          
          // Override onClose to resolve with false
          setTimeout(() => {
            const modal = document.querySelector('[role="dialog"]');
            if (modal) {
              modal.addEventListener('click', (e) => {
                if (e.target === modal || e.target.closest('button[aria-label="Close"]')) {
                  setConfirmModal({ show: false, title: 'Confirm Action', message: '', confirmText: 'Confirm', variant: 'danger', onConfirm: null });
                  setIsProcessing(false);
                  resolve(false);
                }
              });
            }
          }, 100);
        });
      } else if (currentStatus === 'pending' && newStatus === 'paid') {
        // Mark-as-paid: warn that the current org schedule + break will be locked
        const employee = paymentData.find(emp => emp.employeeId === employeeId);
        const employeeName = employee?.employeeName || 'this employee';
        const netPay = (employee?.netPay || 0).toFixed(2);
        const hourlyRate = (employee?.hourlyRate || 0).toFixed(2);
        
        return new Promise((resolve) => {
          setConfirmModal({
            show: true,
            title: 'Mark as Paid?',
            variant: 'info',
            confirmText: 'Mark as Paid',
            message: `Mark ${employeeName} as paid (£${netPay} at £${hourlyRate}/hr)?\n\n🔒 This will LOCK the following snapshot to this pay run:\n  • Hourly rate\n  • Work schedule (day-specific hours)\n  • Daily break duration\n\nFuture edits to the organisation's schedule will NOT retroactively change this paid run. If you've recently edited the schedule, make sure it reflects the period being paid before continuing.`,
            onConfirm: async () => {
              setConfirmModal({ show: false, title: 'Confirm Action', message: '', confirmText: 'Confirm', variant: 'danger', onConfirm: null });
              const ok = await performToggle(employeeId, newStatus, paymentDate);
              resolve(ok);
            }
          });
          
          setTimeout(() => {
            const modal = document.querySelector('[role="dialog"]');
            if (modal) {
              modal.addEventListener('click', (e) => {
                if (e.target === modal || e.target.closest('button[aria-label="Close"]')) {
                  setConfirmModal({ show: false, title: 'Confirm Action', message: '', confirmText: 'Confirm', variant: 'danger', onConfirm: null });
                  setIsProcessing(false);
                  resolve(false);
                }
              });
            }
          }, 100);
        });
      } else {
        // No-op (shouldn't normally happen) — just defer to performToggle
        return await performToggle(employeeId, newStatus, paymentDate);
      }
      
    } catch (error) {
      console.error('Error in togglePaymentStatus:', error);
      setIsProcessing(false);
      return false;
    }
  };
  
  // Helper function to perform the actual toggle
  const performToggle = async (employeeId, newStatus, paymentDate) => {
    try {
      
      // Get the week dates for the API call (single week)
      const selectedWeekDates = getWeekDates(selectedYear, selectedWeek);
      
      // Call the API to persist the payment status change
      await axios.post(`${API}/admin/payment-status/toggle`, {
        employee_id: employeeId,
        payment_status: newStatus,
        payment_date: paymentDate,
        week_start: selectedWeekDates[0].date,
        week_end: selectedWeekDates[6].date
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Update local state after successful API call
      setPaymentData(prev => prev.map(emp => 
        emp.employeeId === employeeId 
          ? { ...emp, paymentStatus: newStatus, paymentDate }
          : emp
      ));
      
      // Recalculate stats with the updated data
      const updatedData = paymentData.map(emp => 
        emp.employeeId === employeeId 
          ? { ...emp, paymentStatus: newStatus, paymentDate }
          : emp
      );
      setWeeklyStats(calculateWeeklyStats(updatedData));
      
      
      setIsProcessing(false);
      return true; // Success
      
    } catch (error) {
      console.error('Error updating payment status:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update payment status. Please try again.';
      setErrorModal({
        show: true,
        message: errorMessage
      });
      setIsProcessing(false);
      return false; // Failure
    }
  };

  const markSelectedAsPaid = async () => {
    const selectedPayments = paymentData.filter(emp => 
      selectedRows.has(emp.employeeId) && emp.paymentStatus === 'pending' && emp.netPay > 0
    );
    
    if (selectedPayments.length === 0) {
      setErrorModal({
        show: true,
        message: 'Please select at least one employee with pending payment status and amount due.'
      });
      return;
    }

    const totalAmount = selectedPayments.reduce((s, e) => s + (e.netPay || 0), 0).toFixed(2);
    setConfirmModal({
      show: true,
      title: 'Mark Selected as Paid?',
      variant: 'info',
      confirmText: `Mark ${selectedPayments.length} as Paid`,
      message: `Mark ${selectedPayments.length} selected payment${selectedPayments.length > 1 ? 's' : ''} as paid (£${totalAmount} total)?\n\n🔒 This will LOCK the following snapshot to each pay run:\n  • Hourly rate (per employee)\n  • Work schedule (day-specific hours)\n  • Daily break duration\n\nFuture edits to the organisation's schedule will NOT retroactively change these paid runs. If you've recently edited the schedule, make sure it reflects the period being paid before continuing.`,
      onConfirm: async () => {
        setIsProcessing(true);
        try {
          // Get current week dates for overtime lookup
          const selectedWeekDates = getWeekDates(selectedYear, selectedWeek);
          const startDate = selectedWeekDates[0].date;
          const endDate = selectedWeekDates[6].date;
          const paymentDate = new Date().toISOString().split('T')[0];
          
          // Step 1: Mark payments as paid — call performToggle directly to
          // skip the per-employee confirm dialog (we've already shown the
          // bulk confirm above with the snapshot-lock warning).
          for (const employee of selectedPayments) {
            console.log('Calling performToggle for', employee.employeeName);
            const success = await performToggle(employee.employeeId, 'paid', paymentDate);
            console.log('performToggle result:', success ? 'SUCCESS' : 'FAILED');
            
            if (!success) {
              // Toggle failed (e.g., pending overtime) - stop here
              console.log('=== EXITING DUE TO TOGGLE FAILURE ===');
              setIsProcessing(false);
              return;
            }
          }
          
          // Step 2: Fetch and mark related overtime as paid
          try {
            const overtimeResponse = await axios.get(`${API}/admin/overtime/for-pay-period`, {
              params: { start_date: startDate, end_date: endDate }
            });
            
            if (overtimeResponse.data && overtimeResponse.data.length > 0) {
              const overtimeIds = overtimeResponse.data.map(ot => ot.id);
              
              console.log('Marking', overtimeIds.length, 'overtime requests as paid');
              // Mark overtime as paid
              await axios.post(`${API}/admin/overtime/mark-paid`, {
                overtime_ids: overtimeIds
              });
              
              console.log(`Successfully marked ${overtimeIds.length} overtime requests as paid`);
            }
          } catch (overtimeError) {
            console.error('OVERTIME ERROR CAUGHT:', overtimeError);
            const overtimeErrorMessage = overtimeError.response?.data?.detail || 'There was an issue updating overtime status.';
            
            setSuccessModal({ show: false, message: '' });
            setErrorModal({
              show: true,
              message: `Payments marked as paid successfully, but ${overtimeErrorMessage} Please check overtime records manually.`
            });
            setIsProcessing(false);
            setSelectedRows(new Set());
            setSelectAll(false);
            console.log('=== EXITING DUE TO OVERTIME ERROR ===');
            return;
          }
          
          // Clear selection after processing
          setSelectedRows(new Set());
          setSelectAll(false);
          
          setSuccessModal({
            show: true,
            message: `Successfully marked ${selectedPayments.length} payments as paid.`
          });
          setIsProcessing(false);
        } catch (error) {
          console.error('OUTER CATCH BLOCK - ERROR:', error);
          const errorMessage = error.response?.data?.detail || error.message || 'Failed to mark payments as paid. Please try again.';
          
          // Clear any success modal that might have been set
          setSuccessModal({ show: false, message: '' });
          
          setErrorModal({
            show: true,
            message: errorMessage
          });
          setIsProcessing(false);
          return;
        }
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  // Convert decimal hours -> "Nh Mm". Uses Math.round on total minutes
  // (matches the precision pattern in workingHoursHelpers and the mobile
  // equivalent in PaymentDetailScreen — avoids float drift).
  const formatBreakDeducted = (decimalHours) => {
    const totalMinutes = Math.max(0, Math.round((Number(decimalHours) || 0) * 60));
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h === 0 && m === 0) return '0m';
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      // Only select employees with amounts due (netPay > 0)
      const eligibleEmployeeIds = paymentData
        .filter(emp => emp.netPay > 0)
        .map(emp => emp.employeeId);
      setSelectedRows(new Set(eligibleEmployeeIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleRowSelect = (employeeId, checked) => {
    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(employeeId);
    } else {
      newSelectedRows.delete(employeeId);
    }
    setSelectedRows(newSelectedRows);
    
    // Update select all checkbox state
    setSelectAll(newSelectedRows.size === paymentData.length && paymentData.length > 0);
  };

  // Calendar component - Dark Theme
  const Calendar = ({ currentDate, onDateClick, onClose, selectedYear, selectedWeek, isFromCalendar = true }) => {
    const [displayDate, setDisplayDate] = useState(currentDate);
    const { weeks, month, year } = generateCalendar(displayDate);
    const calendarRef = React.useRef(null);
    
    // Close calendar when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
          onClose();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [onClose]);
    
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const navigateMonth = (direction) => {
      const newDate = new Date(displayDate);
      newDate.setMonth(newDate.getMonth() + direction);
      setDisplayDate(newDate);
    };
    
    const isSelectedWeek = (date) => {
      const weekNum = getWeekNumber(date);
      return date.getFullYear() === selectedYear && weekNum === selectedWeek;
    };
    
    return (
      <div className="relative">
        <div className={`absolute z-[9999] mt-2 ${isFromCalendar ? 'left-0' : 'right-0'}`}>
          <div 
            ref={calendarRef}
            className="bg-slate-800 rounded-xl shadow-xl border border-slate-600 p-6 w-80"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Select a Week</h3>
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-300 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h4 className="text-lg font-semibold text-white">
                {monthNames[month]} {year}
              </h4>
              <button 
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-400 py-2">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {weeks.map((week, weekIndex) => (
                week.map((date, dayIndex) => {
                  const isCurrentMonth = date.getMonth() === month;
                  const isSelected = isSelectedWeek(date);
                  
                  return (
                    <button
                      key={`${weekIndex}-${dayIndex}`}
                      onClick={() => onDateClick(date)}
                      className={`p-2 text-sm rounded-lg transition-colors ${
                        isCurrentMonth ? 'text-white' : 'text-gray-400'
                      } ${
                        isSelected 
                          ? 'bg-purple-600 text-white' 
                          : 'hover:bg-slate-700'
                      }`}
                    >
                      {date.getDate()}
                    </button>
                  );
                })
              ))}
            </div>
            
            <div className="mt-4 text-sm text-gray-400">
              Click on any day to select that week
            </div>
          </div>
        </div>
      </div>
    );
  };



  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Team Payments</h1>
        <p className="text-purple-200">Manage team payments, payment processing, and financial tracking</p>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-white ml-3">Filters & Controls</h2>
          </div>
          
          {/* Current Week Button - Only active when not on current week */}
          <button
            onClick={goToCurrentWeek}
            disabled={isCurrentWeekSelected()}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm flex items-center ${
              isCurrentWeekSelected()
                ? 'bg-slate-700 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-500 text-white'
            }`}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            Current Week
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Week Selection with Navigation */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              Week Selection
            </h4>
            <div className="flex items-center space-x-2">
              {/* Previous Week Button */}
              <button
                onClick={goToPreviousWeek}
                className="p-2 bg-slate-800 border border-slate-600 rounded-lg text-gray-300 hover:bg-slate-700 hover:text-white transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Week Display Button */}
              <button
                onClick={() => setShowCalendar(true)}
                className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center hover:bg-slate-700"
              >
                <span>{formatWeekDisplay(selectedYear, selectedWeek)}</span>
              </button>
              
              {/* Next Week Button */}
              <button
                onClick={goToNextWeek}
                className="p-2 bg-slate-800 border border-slate-600 rounded-lg text-gray-300 hover:bg-slate-700 hover:text-white transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {/* Calendar */}
            {showCalendar && (
              <Calendar
                currentDate={calendarDate}
                onDateClick={(date) => handleDateClick(date)}
                onClose={() => setShowCalendar(false)}
                selectedYear={selectedYear}
                selectedWeek={selectedWeek}
                isFromCalendar={true}
              />
            )}
          </div>

          {/* Status Filter - Pill Selector */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Payment Status
            </h4>
            <div className="flex rounded-lg bg-slate-800 p-1 border border-slate-600">
              <button
                onClick={() => setSelectedStatus('all')}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  selectedStatus === 'all'
                    ? 'bg-purple-500 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedStatus('pending')}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  selectedStatus === 'pending'
                    ? 'bg-yellow-500 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                Unpaid
              </button>
              <button
                onClick={() => setSelectedStatus('paid')}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  selectedStatus === 'paid'
                    ? 'bg-green-500 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                Paid
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-500/20 text-blue-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Team Members</p>
              <p className="text-2xl font-bold text-white">{weeklyStats.employeeCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-500/20 text-yellow-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Pending Payments</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(weeklyStats.totalPending)}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-500/20 text-green-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Paid This Week</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(weeklyStats.totalPaid)}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-500/20 text-purple-400">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Total Amount Due</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(weeklyStats.totalAmountDue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Weekly Earnings & Payments</h2>
          {(() => {
            // Check if we have eligible items selected (non-zero net pay)
            const eligibleSelectedItems = paymentData.filter(emp => 
              selectedRows.has(emp.employeeId) && emp.paymentStatus === 'pending' && emp.netPay > 0
            );
            
            return eligibleSelectedItems.length > 0 && (
              <button
                onClick={markSelectedAsPaid}
                disabled={isProcessing}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center"
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Mark Selected as Paid ({eligibleSelectedItems.length})
                  </>
                )}
              </button>
            );
          })()}
        </div>
        
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mr-3"></div>
              <span className="text-gray-400">Loading payment data...</span>
            </div>
          ) : paymentData.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No payment data found</h3>
              <p className="text-gray-400">Try adjusting your filters or date range.</p>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Sub-Contractor</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">CIS Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Sites</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Earnings</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Expenses</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Net Pay</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">Overrides</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Payment Status</th>
                </tr>
              </thead>
              <tbody>
                {paymentData.map((employee, index) => (
                  <tr 
                    key={employee.id} 
                    className={`hover:bg-slate-700/50 cursor-pointer transition-colors ${index !== paymentData.length - 1 ? 'border-b border-slate-700/50' : ''}`}
                    onClick={() => openPaymentModal(employee)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(employee.employeeId)}
                        onChange={(e) => handleRowSelect(employee.employeeId, e.target.checked)}
                        disabled={employee.netPay <= 0}
                        className={`w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500 ${
                          employee.netPay <= 0 ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {employee.profilePhotoUrl ? (
                            <img
                              src={employee.profilePhotoUrl}
                              alt={employee.employeeName}
                              className="h-10 w-10 rounded-full object-cover ring-2 ring-purple-500/40"
                              data-testid={`payment-employee-avatar-${employee.id}`}
                            />
                          ) : (
                            <div
                              className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-medium text-sm"
                              data-testid={`payment-employee-avatar-fallback-${employee.id}`}
                            >
                              {employee.employeeName.split(' ').map(n => n[0]).join('')}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{employee.employeeName}</div>
                          <div className="text-sm text-gray-400">{employee.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          employee.cis_registered 
                            ? 'bg-green-500/20 text-green-300 border border-green-500/30' 
                            : 'bg-slate-600 text-gray-300'
                        }`}>
                          {employee.cis_registered ? 'CIS Registered' : 'Not Registered'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        {employee.allSites && employee.allSites.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {employee.allSites.map((siteName, index) => (
                              <span 
                                key={index}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30"
                              >
                                {siteName}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-600 text-gray-400">
                            No billable time
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        <div>Regular: {employee.regularHours}h</div>
                        <div className="text-orange-400">Overtime: {employee.overtimeHours}h</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        <div>Regular: {formatCurrency(employee.regularPay)}</div>
                        <div className="text-orange-400">Overtime: {formatCurrency(employee.overtimePay)}</div>
                        <div className="font-medium text-white">Gross: {formatCurrency(employee.totalGrossPay)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        {employee.approvedExpenses > 0 ? (
                          <div>
                            <span className="text-green-400">{formatCurrency(employee.approvedExpenses)}</span>
                            <div className="text-xs text-gray-400">{employee.expenses.length} item(s)</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">No expenses</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="font-bold text-lg text-white">{formatCurrency(employee.netPay)}</div>
                        <div className="text-xs text-gray-400">After deductions: {formatCurrency(employee.totalDeductions)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {employee.hasOverrides ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Modified
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center">
                        {employee.netPay > 0 ? (
                          <>
                            {/* Toggle Switch */}
                            <button
                              onClick={() => togglePaymentStatus(employee.employeeId, employee.paymentStatus)}
                              disabled={isProcessing}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-50 ${
                                employee.paymentStatus === 'paid'
                                  ? 'bg-green-600'
                                  : 'bg-slate-600'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  employee.paymentStatus === 'paid' ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <span className="ml-3 text-sm font-medium text-white">
                              {employee.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-gray-400 italic">No payment due</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Payment Breakdown Modal */}
      {showPaymentModal && selectedPayment && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={closePaymentModal}
        >
          <div 
            className="bg-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 p-6 text-white rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {selectedPayment.profilePhotoUrl ? (
                    <img
                      src={selectedPayment.profilePhotoUrl}
                      alt={selectedPayment.employeeName}
                      className="h-12 w-12 rounded-full object-cover ring-2 ring-white/40"
                      data-testid="payment-modal-employee-avatar"
                    />
                  ) : (
                    <div
                      className="h-12 w-12 rounded-full bg-white/15 flex items-center justify-center text-white font-semibold"
                      data-testid="payment-modal-employee-avatar-fallback"
                    >
                      {selectedPayment.employeeName.split(' ').map(n => n[0]).join('')}
                    </div>
                  )}
                  <div>
                    <h2 className="text-xl font-bold">Payment Breakdown</h2>
                    <p className="text-purple-200 mt-1">{selectedPayment.employeeName}</p>
                  </div>
                </div>
                <button 
                  onClick={closePaymentModal}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Reopened Pay Run Notice */}
            {selectedPayment.pay_run_reopened && (
              <div className="mx-6 mt-4 mb-4 bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-amber-300">Pay Run Recalculated</h4>
                    <p className="text-xs text-amber-200 mt-1">
                      This payment run was reopened after being marked as paid. Calculations may differ from the original payment because they now use the CURRENT hourly rate
                      (£{selectedPayment.hourlyRate?.toFixed(2)}){selectedPayment.locked_hourly_rate != null ? <> instead of the original locked rate (£{selectedPayment.locked_hourly_rate.toFixed(2)})</> : null}, AND the CURRENT work schedule + break duration instead of the schedule locked at the time of payment.
                    </p>
                    <p className="text-xs text-amber-200/80 mt-2">
                      Inconsistencies may exist with expense and overtime payments. Mark the week as paid again to lock a fresh snapshot.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="p-6">
              {/* Week Information */}
              <div className="bg-slate-800 rounded-lg p-4 mb-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-2">Payment Period</h3>
                <p className="text-gray-400">
                  Week {selectedWeek}, {selectedYear} ({formatWeekDisplay(selectedYear, selectedWeek).split('(')[1]?.replace(')', '') || ''})
                </p>
              </div>

              {/* Earnings Breakdown */}
              <div className="space-y-6">
                {/* Regular Hours */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xs font-semibold text-white mb-1">Regular Hours</h4>
                      <p className="text-xs text-gray-400">Base working hours at standard rate</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-semibold text-white mb-1">
                        {overrides.regularHours.enabled ? (
                          <input
                            type="number"
                            step="0.01"
                            value={overrides.regularHours.value}
                            onChange={(e) => handleOverrideValueChange('regularHours', e.target.value)}
                            className="w-24 px-3 py-2 border-2 border-blue-500 rounded-lg text-right text-xs font-semibold bg-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                          />
                        ) : (
                          <span>{selectedPayment.regularHours}h</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 flex items-center justify-end gap-1.5">
                        <span>@ {formatCurrency(selectedPayment.using_locked_rate && selectedPayment.locked_hourly_rate != null ? selectedPayment.locked_hourly_rate : selectedPayment.hourlyRate)}/hour</span>
                        {selectedPayment.using_locked_rate && selectedPayment.locked_hourly_rate != null && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-purple-500/20 border border-purple-500/40 text-purple-300"
                            data-testid="locked-rate-chip"
                            title="This rate was locked when this pay run was marked as paid."
                          >
                            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c-1.104 0-2 .896-2 2v3h4v-3c0-1.104-.896-2-2-2zm6 5V9a6 6 0 10-12 0v7H4v5h16v-5h-2z" />
                            </svg>
                            Locked
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Override Control Section */}
                  <div className={`border rounded-lg p-4 transition-all duration-200 ${
                    overrides.regularHours.enabled 
                      ? 'bg-blue-500/10 border-blue-500/30 shadow-md' 
                      : 'bg-slate-700 border-slate-600'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleOverrideToggle('regularHours')}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                            overrides.regularHours.enabled
                              ? 'bg-blue-600 text-white shadow-lg'
                              : 'bg-slate-600 text-gray-300 hover:bg-slate-500'
                          }`}
                        >
                          <svg className={`w-4 h-4 ${overrides.regularHours.enabled ? 'text-white' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>{overrides.regularHours.enabled ? 'Override Active' : 'Override Hours'}</span>
                        </button>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white">
                          = {formatCurrency(selectedPayment.regularPay)}
                        </div>
                      </div>
                    </div>
                    
                    {overrides.regularHours.enabled && (
                      <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <svg className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L5.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-amber-300">Manual Override Active</p>
                            <p className="text-xs text-amber-200 mt-1">
                              Original calculated: <strong>{selectedPayment.originalRegularHours}h</strong> 
                              ({formatCurrency(selectedPayment.originalRegularHours * selectedPayment.hourlyRate)})
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Overtime Hours */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xs font-semibold text-white mb-1">Overtime Hours</h4>
                      <p className="text-xs text-gray-400">Overtime hours at standard rate</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-semibold text-white mb-1">
                        {overrides.overtimeHours.enabled ? (
                          <input
                            type="number"
                            step="0.01"
                            value={overrides.overtimeHours.value}
                            onChange={(e) => handleOverrideValueChange('overtimeHours', e.target.value)}
                            className="w-24 px-3 py-2 border-2 border-blue-500 rounded-lg text-right text-xs font-semibold bg-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                          />
                        ) : (
                          <span>{selectedPayment.overtimeHours}h</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">@ {formatCurrency(selectedPayment.overtimeRate)}/hour</div>
                    </div>
                  </div>
                  
                  {/* Override Control Section */}
                  <div className={`border rounded-lg p-4 transition-all duration-200 ${
                    overrides.overtimeHours.enabled 
                      ? 'bg-blue-500/10 border-blue-500/30 shadow-md' 
                      : 'bg-slate-700 border-slate-600'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleOverrideToggle('overtimeHours')}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                            overrides.overtimeHours.enabled
                              ? 'bg-blue-600 text-white shadow-lg'
                              : 'bg-slate-600 text-gray-300 hover:bg-slate-500'
                          }`}
                        >
                          <svg className={`w-4 h-4 ${overrides.overtimeHours.enabled ? 'text-white' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>{overrides.overtimeHours.enabled ? 'Override Active' : 'Override Hours'}</span>
                        </button>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white">
                          = {formatCurrency(selectedPayment.overtimePay)}
                        </div>
                      </div>
                    </div>
                    
                    {overrides.overtimeHours.enabled && (
                      <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <svg className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L5.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-amber-300">Manual Override Active</p>
                            <p className="text-xs text-amber-200 mt-1">
                              Original calculated: <strong>{selectedPayment.originalOvertimeHours}h</strong> 
                              ({formatCurrency(selectedPayment.originalOvertimeHours * selectedPayment.overtimeRate)})
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* CIS Deductions - Only shown for CIS registered users */}
                {selectedPayment.cis_registered && (
                  <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="text-xs font-semibold text-white mb-1">CIS Tax Deductions</h4>
                        <p className="text-xs text-gray-400">20% deduction on gross earnings (fixed rate)</p>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-semibold text-red-600 mb-1">
                          -{formatCurrency(selectedPayment.cisDeduction)}
                        </div>
                        <div className="text-xs text-gray-400">20% of gross earnings</div>
                      </div>
                    </div>
                    
                    {/* Information Section (no override functionality) */}
                    <div className="bg-slate-700 border border-slate-600 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-3 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-300 font-medium text-sm">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>CIS Registered Employee</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-400 mb-1">
                            Gross Earnings: {formatCurrency(selectedPayment.totalGrossPay)}
                          </div>
                          <div className="text-xs font-semibold text-red-400">
                            CIS Deduction: -{formatCurrency(selectedPayment.cisDeduction)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-blue-300">Construction Industry Scheme (CIS)</p>
                            <p className="text-xs text-blue-200 mt-1">
                              This employee is registered under CIS. A 20% tax deduction is automatically applied to gross earnings and cannot be overridden.
                              {selectedPayment.utr_number && (
                                <><br/>UTR: {selectedPayment.utr_number}</>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Approved Expenses */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xs font-semibold text-white mb-1">Approved Expenses</h4>
                      <p className="text-xs text-gray-400">Reimbursable expenses and costs</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-semibold text-white mb-1">
                        {overrides.expenses.enabled ? (
                          <input
                            type="number"
                            step="0.01"
                            value={overrides.expenses.value}
                            onChange={(e) => handleOverrideValueChange('expenses', e.target.value)}
                            className="w-28 px-3 py-2 border-2 border-blue-500 rounded-lg text-right text-xs font-semibold bg-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                          />
                        ) : (
                          <span>{formatCurrency(selectedPayment.approvedExpenses)}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">{selectedPayment.expenses?.length || 0} item(s)</div>
                    </div>
                  </div>

                  {/* Expense Details List */}
                  {selectedPayment.expenses && selectedPayment.expenses.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h5 className="text-xs font-semibold text-gray-300 mb-3">Expense Details:</h5>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {selectedPayment.expenses.map((expense, index) => (
                          <div key={expense.id || index} className="bg-slate-700 rounded-lg p-3 border border-slate-600">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="text-xs font-medium text-white mb-1">
                                  {expense.description || 'No description'}
                                </div>
                                <div className="flex items-center space-x-3 text-xs text-gray-400">
                                  <span className="bg-slate-600 px-2 py-1 rounded-full">
                                    {expense.category || 'General'}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right ml-3">
                                <div className="text-xs font-semibold text-white">
                                  {formatCurrency(expense.amount || 0)}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {expense.status === 'approved' ? '✓ Approved' : expense.status}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Expenses Message */}
                  {(!selectedPayment.expenses || selectedPayment.expenses.length === 0) && (
                    <div className="mt-4 p-3 bg-slate-700 rounded-lg text-center">
                      <div className="text-xs text-gray-400">No approved expenses for this pay period</div>
                    </div>
                  )}
                  
                  {/* Override Control Section */}
                  <div className={`border rounded-lg p-4 transition-all duration-200 ${
                    overrides.expenses.enabled 
                      ? 'bg-blue-500/10 border-blue-500/30 shadow-md' 
                      : 'bg-slate-700 border-slate-600'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleOverrideToggle('expenses')}
                          className={`flex items-center space-x-3 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                            overrides.expenses.enabled
                              ? 'bg-blue-600 text-white shadow-lg'
                              : 'bg-slate-600 text-gray-300 hover:bg-slate-500'
                          }`}
                        >
                          <svg className={`w-4 h-4 ${overrides.expenses.enabled ? 'text-white' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>{overrides.expenses.enabled ? 'Override Active' : 'Override Amount'}</span>
                        </button>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white">
                          = {formatCurrency(selectedPayment.approvedExpenses)}
                        </div>
                      </div>
                    </div>
                    
                    {overrides.expenses.enabled && (
                      <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <svg className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L5.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-amber-300">Manual Override Active</p>
                            <p className="text-xs text-amber-200 mt-1">
                              Original calculated: <strong>{formatCurrency(overrides.expenses.original)}</strong>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Total Calculation - Use backend values */}
                <div className="border-t-2 border-slate-600 pt-4">
                  {/* Break deduction transparency row (Pass W1). Hidden when
                      break_hours_deducted is 0/null/undefined so we don't
                      clutter the breakdown for orgs with no break configured. */}
                  {selectedPayment.break_hours_deducted > 0 && (
                    <div className="flex justify-between items-center text-sm mb-3 px-4" data-testid="break-deducted-row">
                      <span className="text-red-400">
                        Break deducted
                        {selectedPayment.enabled_days_worked > 0 && (
                          <span className="text-red-300/70 ml-2 text-xs font-normal">
                            (across {selectedPayment.enabled_days_worked} {selectedPayment.enabled_days_worked === 1 ? 'day' : 'days'})
                          </span>
                        )}
                      </span>
                      <span className="text-red-400 font-medium">
                        -{formatBreakDeducted(selectedPayment.break_hours_deducted)}
                      </span>
                    </div>
                  )}
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <div className="flex justify-between items-center text-sm font-semibold">
                      <span className="text-white">Net Pay</span>
                      <span className="text-green-400 text-lg">
                        {formatCurrency(selectedPayment.netPay)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {selectedPayment.cis_registered ? (
                        <>CIS deduction: -{formatCurrency(selectedPayment.cisDeduction)}</>
                      ) : (
                        'No deductions applicable'
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 pt-4 border-t border-slate-700">
                {/* Save Success/Error Messages */}
                {saveSuccess && (
                  <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm font-medium text-green-300">Overrides saved successfully!</p>
                    </div>
                  </div>
                )}
                
                {saveError && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm font-medium text-red-300">{saveError}</p>
                    </div>
                  </div>
                )}
                
                {/* Override Reason Field - Shows when any override is active */}
                {hasActiveOverrides() && (
                  <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-center space-x-2 mb-3">
                      <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <label className="text-sm font-medium text-amber-300">
                        Reason for Override <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <textarea
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Please provide a reason for the override changes (required)"
                      className={`w-full p-3 border rounded-lg resize-none text-sm bg-slate-800 text-white placeholder-gray-500 ${
                        hasActiveOverrides() && !overrideReason.trim() && saveError
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : 'border-slate-600 focus:ring-purple-500 focus:border-purple-500'
                      }`}
                      rows={3}
                      maxLength={500}
                      required
                    />
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-xs text-amber-200">
                        This reason will be logged for audit purposes
                      </p>
                      <p className="text-xs text-gray-400">
                        {overrideReason.length}/500 characters
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end items-center">
                  <div className="flex items-center space-x-3">
                    {/* Unsaved Changes Indicator */}
                    {hasUnsavedChanges() && !saveSuccess && (
                      <div className="flex items-center space-x-2 text-amber-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L5.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <span className="text-sm font-medium">Unsaved changes</span>
                      </div>
                    )}
                    
                    {/* Save Changes Button */}
                    <button
                      onClick={saveOverrides}
                      disabled={isSaving || (!hasUnsavedChanges() && !saveSuccess) || (hasActiveOverrides() && !overrideReason.trim())}
                      className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 ${
                        isSaving
                          ? 'bg-blue-400 text-white cursor-not-allowed'
                          : hasUnsavedChanges() && (!hasActiveOverrides() || overrideReason.trim())
                          ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
                          : 'bg-gray-300 text-gray-400 cursor-not-allowed'
                      }`}
                      title={hasActiveOverrides() && !overrideReason.trim() ? 'Please provide a reason for the override' : ''}
                    >
                      {isSaving ? (
                        <>
                          <svg className="animate-spin w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                          </svg>
                          <span>Save Changes</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.show}
        title={confirmModal.title || 'Confirm Action'}
        message={confirmModal.message}
        variant={confirmModal.variant || 'danger'}
        confirmText={confirmModal.confirmText || 'Confirm'}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ show: false, title: 'Confirm Action', message: '', confirmText: 'Confirm', variant: 'danger', onConfirm: null });
        }}
        onClose={() => setConfirmModal({ show: false, title: 'Confirm Action', message: '', confirmText: 'Confirm', variant: 'danger', onConfirm: null })}
      />

      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
    </div>
  );
};

export default AdminPaymentsPage;